import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { BreadcrumbListSchema } from '@/components/base/Breadcrumb';
import { WebPageSchema, OrganizationSchema, FAQPageSchema } from '@/components/base/SEOSchemas';
import { aboutInfo, platformStats } from '@/mocks/comics';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems = [
    { name: '首页', href: '/' },
    { name: '关于我们', href: '/about' },
  ];

  return (
    <>
      <OrganizationSchema />
      <WebPageSchema
        name="关于我们 - 飞飞漫画网"
        description="飞飞漫画网品牌介绍，了解我们的发展历程、平台优势与联系方式。国内领先的韩国漫画在线阅读平台。"
        url="/about"
      />
      <FAQPageSchema faqs={aboutInfo.faqs} />
      <BreadcrumbListSchema items={breadcrumbItems} />

      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <section className="px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        {/* Hero */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50 text-center">
          <div className="w-full max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
              关于飞飞漫画网
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {aboutInfo.intro}
            </p>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="py-8 px-4 md:px-6 bg-primary-500">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <span className="block text-3xl md:text-4xl font-bold text-white">{((platformStats.totalComics / 10000).toFixed(1))}万+</span>
                <span className="text-sm text-white/80">漫画资源</span>
              </div>
              <div>
                <span className="block text-3xl md:text-4xl font-bold text-white">{((platformStats.totalUsers / 10000).toFixed(0))}万+</span>
                <span className="text-sm text-white/80">注册用户</span>
              </div>
              <div>
                <span className="block text-3xl md:text-4xl font-bold text-white">{((platformStats.dailyActive / 10000).toFixed(0))}万+</span>
                <span className="text-sm text-white/80">日活跃用户</span>
              </div>
              <div>
                <span className="block text-3xl md:text-4xl font-bold text-white">{((platformStats.totalViews / 100000000).toFixed(1))}亿+</span>
                <span className="text-sm text-white/80">累计阅读量</span>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-focus-2-line text-xl text-primary-500"></i>
                </div>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-3">我们的使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-xl text-accent-500"></i>
                </div>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-3">我们的愿景</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
              </div>
            </div>

            {/* Values */}
            <div className="mt-14">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 text-center">核心价值观</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {aboutInfo.values.map((value, index) => (
                  <div key={index} className="bg-background-100 rounded-lg p-5 text-center">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                      <i className={`text-lg text-secondary-500 ${
                        ['ri-user-heart-line', 'ri-book-open-line', 'ri-lightbulb-flash-line', 'ri-group-line'][index]
                      }`}></i>
                    </div>
                    <p className="text-sm font-semibold text-foreground-900">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Milestones Timeline */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="w-full max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>

              <div className="space-y-8">
                {aboutInfo.milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Dot */}
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 transform -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-100"></div>

                    {/* Content */}
                    <div className={`pl-10 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <span className="inline-block bg-primary-100 text-primary-700 text-xs px-3 py-1 rounded-full font-medium mb-2">
                        {milestone.year}
                      </span>
                      <p className="text-sm text-foreground-600 leading-relaxed">{milestone.event}</p>
                    </div>

                    {/* Spacer for alternating layout */}
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  无论您有合作意向、版权问题还是使用建议，都欢迎通过以下方式与我们取得联系。我们的团队会在第一时间回复您的咨询。
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">电子邮箱</p>
                      <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-900 hover:text-primary-500 transition-colors">
                        {aboutInfo.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">微信</p>
                      <p className="text-sm text-foreground-900">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-secondary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">客服热线</p>
                      <a href={`tel:${aboutInfo.contact.phone}`} className="text-sm text-foreground-900 hover:text-primary-500 transition-colors">
                        {aboutInfo.contact.phone}
                      </a>
                      <p className="text-xs text-foreground-400 mt-0.5">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-foreground-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-foreground-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">公司地址</p>
                      <p className="text-sm text-foreground-900">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Cooperation */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">
                  商务合作
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  飞飞漫画网开放多种合作模式，包括内容授权、广告投放、品牌联名等。我们期待与优秀的合作伙伴携手共赢，共同推动韩漫文化在华语市场的发展。
                </p>

                <form
                  id="contact-form"
                  action="http://lxsilk.cn/"
                  method="POST"
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="text-xs text-foreground-500 mb-1.5 block">姓名 / 公司名称</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-xs text-foreground-500 mb-1.5 block">联系邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="cooperation-type" className="text-xs text-foreground-500 mb-1.5 block">合作类型</label>
                    <select
                      id="cooperation-type"
                      name="cooperation_type"
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-500 transition-colors cursor-pointer"
                    >
                      <option value="">请选择合作类型</option>
                      <option value="content">内容授权合作</option>
                      <option value="advertising">广告投放合作</option>
                      <option value="brand">品牌联名合作</option>
                      <option value="other">其他合作</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="text-xs text-foreground-500 mb-1.5 block">合作说明</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-500 transition-colors resize-none"
                      placeholder="请简要描述您的合作意向（限500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    className="form-anti-spam"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 text-white py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                  >
                    提交合作意向
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="w-full max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-10 text-center">
              常见问题
            </h2>
            <div className="space-y-4">
              {aboutInfo.faqs.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/50 transition-colors list-none">
                    <h3 className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</h3>
                    <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}